import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { useHistory } from "react-router-dom";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import DashboardLayout from "../../layouts/DashboardLayout";
import StoreBreadcrumbs from "./components/StoreBreadcrumbs";
import GoogleCalendarService from "../../../../service/GoogleCalendarService";
import ScheduleConfigService from "../../../../service/ScheduleConfigService";
import localeText from "../../../../locale";
import "../FinancialHub/components/FinancialTransactionsTable/FinancialTransactionsTable.css";
import GoogleCalendarImg from "../../../images/GoogleCalendar.svg";

const TABS = {
  PRE_CONFIGURATION: "pre-configuration",
  SCHEDULES: "schedules",
  PREFERENCES: "preferences",
};

const StoreSchedulesPage = ({ userTypeInfo, userInfo, currency }) => {
  const history = useHistory();

  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [hasScheduleConfig, setHasScheduleConfig] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const googleCalendarService = useRef(new GoogleCalendarService()).current;
  const scheduleConfigService = useRef(new ScheduleConfigService()).current;
  const [currentTab, setCurrentTab] = useState(TABS.SCHEDULES);
  const [bufferMinutes, setBufferMinutes] = useState(0);
  const [defaultDurationMinutes, setDefaultDurationMinutes] = useState(30);
  const [workStartHour, setWorkStartHour] = useState(9);
  const [workEndHour, setWorkEndHour] = useState(18);
  const [clientInstructions, setClientInstructions] = useState("");
  const [savingConfig, setSavingConfig] = useState(false);
  const { showSuccessToast, showErrorToast } = useToastContext();
  const [selectedDays, setSelectedDays] = useState({
    workOnMonday: false,
    workOnTuesday: false,
    workOnWednesday: false,
    workOnThursday: false,
    workOnFriday: false,
    workOnSaturday: false,
    workOnSunday: false,
  });
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);

  const WEEKDAYS = [
    { key: "workOnMonday", label: localeText("monday") },
    { key: "workOnTuesday", label: localeText("tuesday") },
    { key: "workOnWednesday", label: localeText("wednesday") },
    { key: "workOnThursday", label: localeText("thursday") },
    { key: "workOnFriday", label: localeText("friday") },
    { key: "workOnSaturday", label: localeText("saturday") },
    { key: "workOnSunday", label: localeText("sunday") },
  ];

  const calculateTimeSlots = () => {
    const slots = [];
    const duration = defaultDurationMinutes;
    const buffer = bufferMinutes;
    const slotInterval = duration + buffer;

    for (let hour = workStartHour; hour < workEndHour; hour++) {
      for (let min = 0; min < 60; min += slotInterval) {
        if (hour * 60 + min < workEndHour * 60) {
          const displayHour = Math.floor((hour * 60 + min) / 60);
          const displayMin = (hour * 60 + min) % 60;
          slots.push(
            `${String(displayHour).padStart(2, "0")}:${String(displayMin).padStart(2, "0")}`,
          );
        }
      }
    }
    return slots;
  };

  const handleToggleDay = (dayKey) => {
    setSelectedDays((prev) => ({
      ...prev,
      [dayKey]: !prev[dayKey],
    }));
  };

  const handleSave = async () => {
    try {
      setSavingConfig(true);

      const token = localStorage.getItem("__extraconomy_token");
      const username = localStorage.getItem("__extraconomy_username");

      const payload = {
        bufferMinutes,
        defaultDurationMinutes,
        user: { username: username },
        instructions: clientInstructions,
        workEndHour,
        workStartHour,
        ...selectedDays,
      };

      const response = await scheduleConfigService.postConfig(token, payload);

      if (response && response.messageCode === 200000) {
        showSuccessToast({ detail: "configurationSavedSuccessfully" });
        setHasScheduleConfig(true);
        setCurrentTab(TABS.SCHEDULES);
        await checkScheduleConfig();
      } else {
        showErrorToast({ code: response?.messageCode });
      }
    } catch {
      showErrorToast({ code: 500000 });
    } finally {
      setSavingConfig(false);
    }
  };

  const checkGoogleCalendarConnection = useCallback(async () => {
    try {
      const token = localStorage.getItem("__extraconomy_token");
      const userId = userInfo?.id;

      if (!userId) {
        console.warn("userId não encontrado no userInfo");
        setGoogleCalendarConnected(false);
        return;
      }

      const response = await googleCalendarService.getStatus(token, userId);

      if (response) {
        return response?.connected || false;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Erro ao verificar conexão Google Calendar:", error);
      return false;
    }
  }, [googleCalendarService, userInfo]);

  const checkScheduleConfig = useCallback(async () => {
    try {
      setIsLoadingConfig(true);

      const token = localStorage.getItem("__extraconomy_token");
      const response = await scheduleConfigService.getConfig(token);

      if (response && response.messageCode === 200001 && response.result) {
        const config = response.result;

        setBufferMinutes(config.bufferMinutes || 0);
        setDefaultDurationMinutes(config.defaultDurationMinutes || 30);
        setWorkStartHour(config.workStartHour || 9);
        setWorkEndHour(config.workEndHour || 18);
        setClientInstructions(config.instructions || "");

        setSelectedDays({
          workOnMonday: config.workOnMonday || false,
          workOnTuesday: config.workOnTuesday || false,
          workOnWednesday: config.workOnWednesday || false,
          workOnThursday: config.workOnThursday || false,
          workOnFriday: config.workOnFriday || false,
          workOnSaturday: config.workOnSaturday || false,
          workOnSunday: config.workOnSunday || false,
        });

        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Erro ao verificar configuração de agendamento:", error);
      return false;
    } finally {
      setIsLoadingConfig(false);
    }
  }, [scheduleConfigService]);

  useEffect(() => {
    const loadData = async () => {
      setCheckingStatus(true);

      const googleCalendarStatus = await checkGoogleCalendarConnection();
      const scheduleConfigStatus = await checkScheduleConfig();

      setGoogleCalendarConnected(googleCalendarStatus);
      setHasScheduleConfig(scheduleConfigStatus);

      if (googleCalendarStatus && scheduleConfigStatus) {
        setCurrentTab(TABS.SCHEDULES);
      } else if (googleCalendarStatus && !scheduleConfigStatus) {
        setCurrentTab(TABS.PRE_CONFIGURATION);
      } else {
        setCurrentTab(TABS.PRE_CONFIGURATION);
      }

      setCheckingStatus(false);
    };

    loadData();
  }, [checkGoogleCalendarConnection, checkScheduleConfig]);

  const handleGoToIntegrations = () => {
    history.push("/dashboard-store/settings?tab=integrations");
  };

  const goToAvailability = async () => {
    setCurrentTab(TABS.PREFERENCES);
    await checkScheduleConfig();
  };

  return (
    <DashboardLayout>
      <StoreBreadcrumbs />

      {checkingStatus ? (
        <div className="flex justify-content-center align-items-center py-8">
          <i className="pi pi-spin pi-spinner text-4xl" style={{ color: "var(--theme-primary)" }} />
        </div>
      ) : (
        <>
          {currentTab === TABS.PRE_CONFIGURATION && (
            <div className="mt-5">
              <p
                className="text-2xl mb-3 text-center font-semibold"
                style={{ color: "var(--theme-text)" }}
              >
                {googleCalendarConnected
                  ? localeText("integrationComplete")
                  : localeText("integrationIncomplete")}
              </p>

              {googleCalendarConnected ? (
                <>
                  <p
                    className="text-center mb-0"
                    style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                  >
                    {localeText("yourAccountIsNowConnected")}
                  </p>
                  <p className="text-center" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                    {localeText("whatsappBotReady")}
                  </p>
                </>
              ) : (
                <p
                  className="text-center mb-0"
                  style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                >
                  {localeText("youNeedToCompleteTheFollowingSteps")}
                </p>
              )}

              <div className="mt-4 md:px-4">
                <div
                  className="flex align-items-start gap-3 p-3 border-round-sm mb-3"
                  style={{
                    backgroundColor: googleCalendarConnected
                      ? "color-mix(in srgb, var(--green-500) 10%, transparent)"
                      : "color-mix(in srgb, var(--theme-border) 35%, transparent)",
                    border: `1px solid ${
                      googleCalendarConnected ? "var(--green-500)" : "var(--theme-border, #EBEBEB)"
                    }`,
                  }}
                >
                  <div
                    className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                    style={{
                      width: "32px",
                      height: "32px",
                      backgroundColor: googleCalendarConnected ? "var(--green-500)" : "#E0E0E0",
                      color: "#fff",
                    }}
                  >
                    {googleCalendarConnected ? (
                      <i className="pi pi-check text-sm" />
                    ) : (
                      <span className="font-semibold text-sm">1</span>
                    )}
                  </div>
                  <div className="flex-grow-1">
                    <div className="flex align-items-center justify-content-between">
                      <div>
                        <h4
                          className="text-sm font-semibold m-0"
                          style={{ color: "var(--theme-text)" }}
                        >
                          {localeText("scheduleStep1Title")}
                        </h4>
                        <p
                          className="text-xs m-0 mt-1"
                          style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                        >
                          {googleCalendarConnected
                            ? localeText("scheduleStep1Connected")
                            : localeText("scheduleStep1Description")}
                        </p>
                      </div>
                      {!googleCalendarConnected && (
                        <Button
                          label={localeText("connect")}
                          icon="pi pi-link"
                          className="p-button-sm"
                          onClick={handleGoToIntegrations}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="p-3 border-round-sm"
                  style={{
                    backgroundColor: hasScheduleConfig
                      ? "color-mix(in srgb, var(--green-500) 10%, transparent)"
                      : googleCalendarConnected
                        ? ""
                        : "color-mix(in srgb, var(--theme-border) 35%, transparent)",
                    border: `1px solid ${
                      hasScheduleConfig ? "var(--green-500)" : "var(--theme-border, #EBEBEB)"
                    }`,
                    opacity: !googleCalendarConnected ? 0.6 : 1,
                  }}
                >
                  <div className="flex align-items-start gap-3 ">
                    <div
                      className={`${!hasScheduleConfig && googleCalendarConnected ? "customStep" : ""} flex align-items-center justify-content-center border-circle flex-shrink-0`}
                      style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: hasScheduleConfig
                          ? "var(--green-500)"
                          : googleCalendarConnected
                            ? "#fff"
                            : "#E0E0E0",
                        color: hasScheduleConfig
                          ? "#fff"
                          : googleCalendarConnected
                            ? "var(--green-500)"
                            : "#fff",
                      }}
                    >
                      {hasScheduleConfig ? (
                        <i className="pi pi-check text-sm" />
                      ) : googleCalendarConnected ? (
                        <span className="font-semibold text-sm">2</span>
                      ) : (
                        <span className="font-semibold text-sm ">2</span>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <div className="flex align-items-center justify-content-between">
                        <div>
                          <h4
                            className="text-sm font-semibold m-0"
                            style={{ color: "var(--theme-text)" }}
                          >
                            {localeText("scheduleStep2Title")}
                          </h4>
                          <p
                            className="text-xs m-0 mt-1"
                            style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                          >
                            {hasScheduleConfig
                              ? localeText("scheduleStep2Configured")
                              : localeText("scheduleStep2Description")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {googleCalendarConnected && (
                    <div className="mt-3">
                      <div>
                        <div className="grid">
                          <div className="col-12 md:col-6">
                            <label className="text-sm font-medium block mb-2">
                              {localeText("scheduleBufferLabel")}
                            </label>
                            <InputNumber
                              value={bufferMinutes}
                              onValueChange={(e) => setBufferMinutes(e.value ?? 0)}
                              min={0}
                              max={180}
                              suffix=" min"
                              useGrouping={false}
                              className="w-full"
                            />
                            <small style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                              {localeText("scheduleBufferHelp")}
                            </small>
                          </div>

                          <div className="col-12 md:col-6">
                            <label className="text-sm font-medium block mb-2">
                              {localeText("standardAppointmentLength")} (min)
                            </label>
                            <InputNumber
                              value={defaultDurationMinutes}
                              onValueChange={(e) => setDefaultDurationMinutes(e.value ?? 0)}
                              min={5}
                              max={480}
                              suffix=" min"
                              useGrouping={false}
                              className="w-full"
                            />
                            <small style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                              {localeText("scheduleAppointmentLengthHelp")}
                            </small>
                          </div>

                          <div className="col-12 md:col-6">
                            <label className="text-sm font-medium block mb-2">
                              {localeText("scheduleWorkStartLabel")}
                            </label>
                            <InputNumber
                              value={workStartHour}
                              onValueChange={(e) => setWorkStartHour(e.value ?? 0)}
                              min={0}
                              max={23}
                              suffix=":00"
                              useGrouping={false}
                              className="w-full"
                            />
                          </div>

                          <div className="col-12 md:col-6">
                            <label className="text-sm font-medium block mb-2">
                              {localeText("scheduleWorkEndLabel")}
                            </label>
                            <InputNumber
                              value={workEndHour}
                              onValueChange={(e) => setWorkEndHour(e.value ?? 0)}
                              min={0}
                              max={23}
                              suffix=":00"
                              useGrouping={false}
                              className="w-full"
                            />
                          </div>
                        </div>
                      </div>

                      <hr />

                      {defaultDurationMinutes && (
                        <div className="mt-4">
                          <p
                            className="text-sm font-medium mb-3"
                            style={{ color: "var(--theme-text)" }}
                          >
                            {localeText("scheduleSelectDays")}
                          </p>
                          <div className="grid gap-2">
                            {WEEKDAYS.map((day) => (
                              <div
                                key={day.key}
                                onClick={() => handleToggleDay(day.key)}
                                className="border-round p-3 cursor-pointer transition-all w-full"
                                style={{
                                  border: `2px solid ${
                                    selectedDays[day.key]
                                      ? "var(--green-500)"
                                      : "var(--theme-border, #EBEBEB)"
                                  }`,
                                  backgroundColor: selectedDays[day.key]
                                    ? "color-mix(in srgb, var(--green-500) 10%, transparent)"
                                    : "var(--surface-card)",
                                  cursor: "pointer",
                                }}
                              >
                                <div className="flex align-items-center justify-content-between mb-2">
                                  <h4
                                    className="text-sm font-semibold m-0"
                                    style={{ color: "var(--theme-text)" }}
                                  >
                                    {day.label}
                                  </h4>
                                  <i
                                    className={`pi ${
                                      selectedDays[day.key] ? "pi-check-circle" : "pi-circle"
                                    }`}
                                    style={{
                                      color: selectedDays[day.key]
                                        ? "var(--green-500)"
                                        : "var(--theme-text-muted)",
                                    }}
                                  />
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {calculateTimeSlots().map((slot) => {
                                    const [h, m] = slot.split(":").map(Number);
                                    const endMinutes = h * 60 + m + defaultDurationMinutes;
                                    const endSlot = `${String(Math.floor(endMinutes / 60)).padStart(2, "0")}:${String(endMinutes % 60).padStart(2, "0")}`;
                                    return (
                                      <span
                                        key={slot}
                                        className="px-2 py-1 border-round text-xs"
                                        style={{
                                          backgroundColor: selectedDays[day.key]
                                            ? "var(--theme-primary, #4caf50)"
                                            : "var(--theme-border, #EBEBEB)",
                                          color: selectedDays[day.key]
                                            ? "#fff"
                                            : "var(--theme-text-muted)",
                                        }}
                                      >
                                        {slot} - {endSlot}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="mt-4">
                            <label
                              className="text-sm font-medium block mb-2"
                              style={{ color: "var(--theme-text)" }}
                            >
                              {localeText("scheduleClientInstructionsLabel")}
                            </label>
                            <InputTextarea
                              value={clientInstructions}
                              onChange={(e) => setClientInstructions(e.target.value)}
                              rows={4}
                              placeholder={localeText("scheduleClientInstructionsPlaceholder")}
                              className="w-full"
                              autoResize
                            />
                            <small style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                              {localeText("scheduleClientInstructionsHelp")}
                            </small>
                          </div>

                          <div className="flex justify-content-end mt-4">
                            <Button
                              label={localeText("save")}
                              icon="pi pi-arrow-right"
                              iconPos="right"
                              className="p-button-sm border-round-md"
                              onClick={handleSave}
                              loading={savingConfig}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div
                  className="flex align-items-start gap-3 p-3 border-round-sm mt-3"
                  style={{
                    backgroundColor:
                      googleCalendarConnected && hasScheduleConfig
                        ? "color-mix(in srgb, var(--theme-primary, #4caf50) 8%, transparent)"
                        : "color-mix(in srgb, var(--theme-border) 35%, transparent)",
                    border: `1px solid ${
                      googleCalendarConnected && hasScheduleConfig
                        ? "var(--theme-primary, #4caf50)"
                        : "var(--theme-border, #EBEBEB)"
                    }`,
                    opacity: googleCalendarConnected && hasScheduleConfig ? 1 : 0.5,
                  }}
                >
                  <div
                    className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                    style={{
                      width: "32px",
                      height: "32px",
                      backgroundColor:
                        googleCalendarConnected && hasScheduleConfig
                          ? "var(--theme-primary, #4caf50)"
                          : "#E0E0E0",
                      color: "#fff",
                    }}
                  >
                    {googleCalendarConnected && hasScheduleConfig ? (
                      <i className="pi pi-calendar text-sm" />
                    ) : (
                      <span className="font-semibold text-sm">3</span>
                    )}
                  </div>
                  <div className="flex-grow-1">
                    <h4
                      className="text-sm font-semibold m-0"
                      style={{ color: "var(--theme-text)" }}
                    >
                      {localeText("scheduleStep3Title")}
                    </h4>
                    <p
                      className="text-xs m-0 mt-1"
                      style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                    >
                      {localeText("scheduleStep3Description")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentTab === TABS.SCHEDULES && (
            <>
              <div className="flex align-items-center justify-content-between gap-4 mb-5 mt-4 flex-wrap">
                <div className="flex align-items-center gap-4 flex-1">
                  <div
                    className="border-circle p-3"
                    style={{
                      border: "1px solid var(--theme-border, #EBEBEB)",
                      width: "54px",
                      height: "54px",
                    }}
                  >
                    <i
                      className="pi pi-calendar text-xl"
                      style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                    />
                  </div>
                  <div>
                    <h2
                      className="text-lg font-semibold m-0"
                      style={{ color: "var(--theme-text)" }}
                    >
                      {localeText("scheduleStep3Title")}
                    </h2>
                    <p
                      className="text-sm m-0 mt-1"
                      style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                    >
                      {localeText("scheduleStep3Description")}
                    </p>
                  </div>
                </div>

                <div className="flex align-items-center gap-3 flex-shrink-0">
                  <a
                    className="hover:underline text-sm flex justify-content-center align-items-center gap-2"
                    href="https://calendar.google.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#5C5C5C" }}
                  >
                    <img src={GoogleCalendarImg} alt="Google Calendar" style={{ width: "20px" }} />
                    <span>{localeText("openYourGoogleCalendar")}</span>
                  </a>

                  <Button
                    label={localeText("availability")}
                    icon="pi pi-cog"
                    className="p-button-outlined flex-1"
                    style={{
                      backgroundColor: "#fff",
                      borderColor: "#EBEBEB",
                      color: "#5C5C5C",
                      borderRadius: "8px",
                    }}
                    onClick={goToAvailability}
                  />
                </div>
              </div>

              {isLoadingConfig ? (
                <div className="flex justify-content-center align-items-center py-8">
                  <i
                    className="pi pi-spin pi-spinner text-4xl"
                    style={{ color: "var(--theme-primary)" }}
                  />
                </div>
              ) : (
                <div style={{ marginTop: "35dvh" }}>
                  <p className="text-center">{localeText("comingSoon")}</p>
                </div>
              )}
            </>
          )}

          {currentTab === TABS.PREFERENCES && (
            <>
              <div className="flex align-items-center justify-content-between gap-4 mb-4 mt-4 flex-wrap">
                <div className="flex align-items-center gap-4 flex-1">
                  <div
                    className="border-circle p-3"
                    style={{
                      border: "1px solid var(--theme-border, #EBEBEB)",
                      width: "54px",
                      height: "54px",
                    }}
                  >
                    <i
                      className="pi pi-calendar text-xl"
                      style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                    />
                  </div>
                  <div>
                    <h2
                      className="text-lg font-semibold m-0"
                      style={{ color: "var(--theme-text)" }}
                    >
                      {localeText("configureYourAvailability")}
                    </h2>
                    <p
                      className="text-sm m-0 mt-1"
                      style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                    >
                      {localeText("defineDaysAndTimes")}
                    </p>
                  </div>
                </div>

                <div className="flex align-items-center gap-3 flex-shrink-0">
                  <a
                    className="hover:underline text-sm flex justify-content-center align-items-center gap-2"
                    href="https://calendar.google.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#5C5C5C" }}
                  >
                    <img src={GoogleCalendarImg} alt="Google Calendar" style={{ width: "20px" }} />
                    <span>{localeText("openYourGoogleCalendar")}</span>
                  </a>

                  <Button
                    label={localeText("storeSchedules")}
                    icon="pi pi-calendar"
                    className="p-button-outlined flex-1"
                    style={{
                      backgroundColor: "#fff",
                      borderColor: "#EBEBEB",
                      color: "#5C5C5C",
                      borderRadius: "8px",
                    }}
                    onClick={() => setCurrentTab(TABS.SCHEDULES)}
                  />
                </div>
              </div>

              <hr />
              {isLoadingConfig ? (
                <div className="flex justify-content-center align-items-center py-8">
                  <i
                    className="pi pi-spin pi-spinner text-4xl"
                    style={{ color: "var(--theme-primary)" }}
                  />
                </div>
              ) : (
                <div
                  className="p-3 border-round-sm"
                  style={{
                    backgroundColor: "",
                    border: "1px solid var(--theme-border, #EBEBEB)",
                    opacity: !googleCalendarConnected ? 0.6 : 1,
                  }}
                >
                  <div className="flex align-items-start gap-3 ">
                    <div className="flex-grow-1">
                      <div className="flex align-items-center justify-content-between">
                        <div>
                          <h4
                            className="text-sm font-semibold m-0"
                            style={{ color: "var(--theme-text)" }}
                          >
                            {localeText("scheduleStep2Title")}
                          </h4>
                          <p
                            className="text-xs m-0 mt-1"
                            style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                          >
                            {hasScheduleConfig
                              ? localeText("scheduleStep2Configured")
                              : localeText("scheduleStep2Description")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {googleCalendarConnected && (
                    <div className="mt-3">
                      <div>
                        <div className="grid">
                          <div className="col-12 md:col-6">
                            <label className="text-sm font-medium block mb-2">
                              {localeText("scheduleBufferLabel")}
                            </label>
                            <InputNumber
                              value={bufferMinutes}
                              onValueChange={(e) => setBufferMinutes(e.value ?? 0)}
                              min={0}
                              max={180}
                              suffix=" min"
                              useGrouping={false}
                              className="w-full"
                            />
                            <small style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                              {localeText("scheduleBufferHelp")}
                            </small>
                          </div>

                          <div className="col-12 md:col-6">
                            <label className="text-sm font-medium block mb-2">
                              {localeText("standardAppointmentLength")} (min)
                            </label>
                            <InputNumber
                              value={defaultDurationMinutes}
                              onValueChange={(e) => setDefaultDurationMinutes(e.value ?? 0)}
                              min={5}
                              max={480}
                              suffix=" min"
                              useGrouping={false}
                              className="w-full"
                            />
                            <small style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                              {localeText("scheduleAppointmentLengthHelp")}
                            </small>
                          </div>

                          <div className="col-12 md:col-6">
                            <label className="text-sm font-medium block mb-2">
                              {localeText("scheduleWorkStartLabel")}
                            </label>
                            <InputNumber
                              value={workStartHour}
                              onValueChange={(e) => setWorkStartHour(e.value ?? 0)}
                              min={0}
                              max={23}
                              suffix=":00"
                              useGrouping={false}
                              className="w-full"
                            />
                          </div>

                          <div className="col-12 md:col-6">
                            <label className="text-sm font-medium block mb-2">
                              {localeText("scheduleWorkEndLabel")}
                            </label>
                            <InputNumber
                              value={workEndHour}
                              onValueChange={(e) => setWorkEndHour(e.value ?? 0)}
                              min={0}
                              max={23}
                              suffix=":00"
                              useGrouping={false}
                              className="w-full"
                            />
                          </div>
                        </div>
                      </div>

                      <hr />

                      {defaultDurationMinutes && (
                        <div className="mt-4">
                          <p
                            className="text-sm font-medium mb-3"
                            style={{ color: "var(--theme-text)" }}
                          >
                            {localeText("scheduleSelectDays")}
                          </p>
                          <div className="grid gap-2">
                            {WEEKDAYS.map((day) => (
                              <div
                                key={day.key}
                                onClick={() => handleToggleDay(day.key)}
                                className="border-round p-3 cursor-pointer transition-all w-full"
                                style={{
                                  border: `2px solid ${
                                    selectedDays[day.key]
                                      ? "var(--green-500)"
                                      : "var(--theme-border, #EBEBEB)"
                                  }`,
                                  backgroundColor: selectedDays[day.key]
                                    ? "color-mix(in srgb, var(--green-500) 10%, transparent)"
                                    : "var(--surface-card)",
                                  cursor: "pointer",
                                }}
                              >
                                <div className="flex align-items-center justify-content-between mb-2">
                                  <h4
                                    className="text-sm font-semibold m-0"
                                    style={{ color: "var(--theme-text)" }}
                                  >
                                    {day.label}
                                  </h4>
                                  <i
                                    className={`pi ${
                                      selectedDays[day.key] ? "pi-check-circle" : "pi-circle"
                                    }`}
                                    style={{
                                      color: selectedDays[day.key]
                                        ? "var(--green-500)"
                                        : "var(--theme-text-muted)",
                                    }}
                                  />
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {calculateTimeSlots().map((slot) => {
                                    const [h, m] = slot.split(":").map(Number);
                                    const endMinutes = h * 60 + m + defaultDurationMinutes;
                                    const endSlot = `${String(Math.floor(endMinutes / 60)).padStart(2, "0")}:${String(endMinutes % 60).padStart(2, "0")}`;
                                    return (
                                      <span
                                        key={slot}
                                        className="px-2 py-1 border-round text-xs"
                                        style={{
                                          backgroundColor: selectedDays[day.key]
                                            ? "var(--theme-primary, #4caf50)"
                                            : "var(--theme-border, #EBEBEB)",
                                          color: selectedDays[day.key]
                                            ? "#fff"
                                            : "var(--theme-text-muted)",
                                        }}
                                      >
                                        {slot} - {endSlot}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="mt-4">
                            <label
                              className="text-sm font-medium block mb-2"
                              style={{ color: "var(--theme-text)" }}
                            >
                              {localeText("scheduleClientInstructionsLabel")}
                            </label>
                            <InputTextarea
                              value={clientInstructions}
                              onChange={(e) => setClientInstructions(e.target.value)}
                              rows={4}
                              placeholder={localeText("scheduleClientInstructionsPlaceholder")}
                              className="w-full"
                              autoResize
                            />
                            <small style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                              {localeText("scheduleClientInstructionsHelp")}
                            </small>
                          </div>

                          <div className="flex justify-content-end mt-4">
                            <Button
                              label={localeText("save")}
                              icon="pi pi-arrow-right"
                              iconPos="right"
                              className="p-button-sm border-round-md"
                              onClick={handleSave}
                              loading={savingConfig}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </DashboardLayout>
  );
};

export default StoreSchedulesPage;
